import React, { useEffect, useState } from "react";
import firebase from "firebase";
import Dragger from "antd/es/upload/Dragger";
import * as queryString from "query-string";

import {
  Affix,
  Breadcrumb,
  Button,
  Col,
  Layout,
  message,
  Popconfirm,
  Row,
  Table,
} from "antd";
import { FileOutlined, FolderOutlined, InboxOutlined } from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UploadRequestOption, RcFile } from "rc-upload/lib/interface";
import { AppRoutes } from "../AppRoutes";
import { CreativeQrCode } from "./CreativeQrCode";

import "./list.scss";

const { Content } = Layout;

export class StorageItem {
  readonly ref: firebase.storage.Reference;
  readonly isFolder: boolean;

  constructor(ref: firebase.storage.Reference, isFolder: boolean) {
    this.ref = ref;
    this.isFolder = isFolder;
  }
}

export interface CreativesListPageProps {}

export const CreativesListPage: React.FC<CreativesListPageProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPath, setCurrentPath] = useState<string>("/");
  const [data, setData] = useState<StorageItem[]>();
  const [isDataLoading, setDataLoading] = useState(false);
  const [targetQrCodeChild, setTargetQrCodeChild] = useState<
    string | undefined
  >();

  const lastPath: string =
    (queryString.parse(location.search)["creative_path"] as string) ?? "";
  if (currentPath !== lastPath) {
    setCurrentPath(lastPath);
  }

  const loadData = (path: string) => {
    firebase
      .storage()
      .ref(path)
      .listAll()
      .then((res) => {
        const result: StorageItem[] = [];
        if (res.prefixes) {
          result.push(...res.prefixes.map((e) => new StorageItem(e, true)));
        }
        if (res.items) {
          result.push(...res.items.map((e) => new StorageItem(e, false)));
        }
        setDataLoading(false);
        setData(result);
      });
  };

  const handleItemClick = (item: StorageItem) => {
    if (item.isFolder) {
      navigate(`${location.pathname}?creative_path=/${item.ref.fullPath}`);
    } else {
      window.open(AppRoutes.CreativeInfo.replace("*", item.ref.fullPath));
      window.focus();
    }
  };

  useEffect(() => {
    setDataLoading(true);
    loadData(lastPath);
  }, [currentPath, lastPath]);

  const breadCrumbs = [...currentPath.split("/")];

  const handleBeforeUpload = (file: RcFile, FileList: RcFile[]) => {
    if (file.size / 1024 / 1024 > 1) {
      message.error("File must smaller than 1MB!");
      return false;
    }
    return true;
  };

  const handleUpload = async (options: UploadRequestOption) => {
    const storage = firebase.storage();
    const storageRef = await storage.ref();
    const imageName = options.filename;
    const imgFile = storageRef.child(`${currentPath}/${imageName}`);
    const actualFile = (options.file as any).originFileObj || options.file;
    try {
      const image = await imgFile.put(actualFile);
      options.onSuccess && options.onSuccess(image, options.file);
      loadData(currentPath);
    } catch {
      options.onError &&
        options.onError(new Error("Upload failed"), options.file);
    }
  };

  return (
    <>
      <Layout className="creatives-page-wrapper">
        <div className="creatives-breadcrumbs-wrapper">
          <Breadcrumb>
            {breadCrumbs.map((e, index) => {
              const subIndex = currentPath.indexOf(`/${e}`);
              const historyPath =
                index > 0
                  ? "?creative_path=" +
                    (currentPath.substring(0, subIndex) + "/" + e)
                  : "";
              return (
                <Breadcrumb.Item key={index}>
                  <Link
                    to={
                      index === breadCrumbs.length - 1
                        ? location.pathname + location.search
                        : location.pathname + historyPath
                    }
                  >
                    {e.trim().length === 0 ? "Root" : e}
                  </Link>
                </Breadcrumb.Item>
              );
            })}
          </Breadcrumb>
        </div>
        <Content className="creatives-list-wrapper">
          <Row gutter={[16, 0]}>
            <Col span={18}>
              <Table<StorageItem>
                dataSource={data}
                loading={isDataLoading}
                pagination={false}
                onRow={(record, rowIndex) => {
                  return {
                    onClick: (event: any) => {
                      // console.log('target: ' + event.target);
                      if (event.target instanceof HTMLSpanElement) {
                        event.preventDefault();
                        return;
                      } else {
                        handleItemClick(record);
                      }
                    },
                  };
                }}
                columns={[
                  {
                    width: 48,
                    render: (item: StorageItem) => {
                      return item.isFolder ? (
                        <FolderOutlined rev={undefined} />
                      ) : (
                        <FileOutlined rev={undefined} />
                      );
                    },
                  },
                  {
                    title: "Name",
                    render: (item: StorageItem) => {
                      return item.ref.name;
                    },
                  },
                  {
                    width: 48,
                    title: "",
                    render: (item: StorageItem) => {
                      if (item.isFolder) {
                        return;
                      }
                      return (
                        <Button
                          type={"link"}
                          onClick={() => {
                            setTargetQrCodeChild(item.ref.fullPath);
                          }}
                        >
                          QR Code
                        </Button>
                      );
                    },
                  },
                  {
                    width: 48,
                    title: "",
                    render: (item: StorageItem) => (
                      <Popconfirm
                        title="Sure to delete?"
                        onConfirm={async () => {
                          return item.ref.delete().then(() => {
                            setData(data?.filter((e) => e !== item));
                          });
                        }}
                      >
                        <Button type={"link"} danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            </Col>
            <Col span={6}>
              <Affix offsetTop={0}>
                <div className="upload-layout-wrapper">
                  <Dragger
                    multiple={true}
                    beforeUpload={handleBeforeUpload}
                    customRequest={handleUpload}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined rev={undefined} />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag file to this area to upload it current
                      folder
                    </p>
                  </Dragger>
                </div>
              </Affix>
            </Col>
          </Row>
        </Content>
      </Layout>
      <CreativeQrCode
        childPath={targetQrCodeChild}
        onCancel={() => setTargetQrCodeChild(undefined)}
      />
    </>
  );
};
